"use client"

import React, { type ReactNode } from "react"
import { ArrowLeft } from "lucide-react"
import { type FieldValues, type SubmitHandler } from "react-hook-form"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { CardHeader } from "@/components/ui/card"
import { useTranslations } from "next-intl"

type StepperContextType = {
  currentStep: string
  setCurrentStep: (step: string) => void
  onSubmit: SubmitHandler<FieldValues>
  setOnSubmit: (onSubmit: SubmitHandler<FieldValues>) => void
}

interface StepperProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode
  defaultValue: string
}

const StepperContext = React.createContext<undefined | StepperContextType>(
  undefined
)

export function Stepper({ children, className, defaultValue }: StepperProps) {
  const [currentStep, setCurrentStep] = React.useState(defaultValue ?? "")
  const [onSubmit, setOnSubmit] = React.useState<SubmitHandler<FieldValues>>(
    () => {
      return (data: FieldValues) => {
        console.log("Default onSubmit, data:", data)
      }
    }
  )

  return (
    <StepperContext.Provider
      value={{ currentStep, setCurrentStep, onSubmit, setOnSubmit }}
    >
      <div className={cn("", className)}>{children}</div>
    </StepperContext.Provider>
  )
}

// Custom Hook to retrieve the current Step
export function useStep() {
  const context = React.useContext(StepperContext)
  if (context === undefined) {
    throw new Error("useStep must be used within a Step")
  }
  return context
}

interface StepProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode
  value: string
}
export function Step({ children, className, value }: StepProps) {
  const { currentStep } = useStep()

  return (
    <div className={cn("", className)}>
      {currentStep === value ? children : null}
    </div>
  )
}

interface StepActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

export function StepActions({ children, className }: StepActionsProps) {
  return <div className={cn("flex justify-end", className)}>{children}</div>
}

export function StepperCardHeader() {
  const { currentStep, setCurrentStep } = useStep()
   const t = useTranslations("Generator")

  return (
    <CardHeader className="py-4 sm:p-6">
      {currentStep !== "info" && (
        <div className="flex items-center gap-x-2">
          <Button
            onClick={() => setCurrentStep("info")}
            variant="secondary"
            size="icon"
            className="size-10 rounded-full"
          >
            <ArrowLeft
              className=""
              size={24}
              strokeWidth={2}
              aria-hidden="true"
            />
          </Button>
          <span className="text-sm text-muted-foreground">{t('Shared.back-to-settings')}</span>
        </div>
      )}
    </CardHeader>
  )
}

// to compile old components for now
export function StepperContentWithHeader({
  children,
}: {
  children: ReactNode
}) {
  return <div>{children}</div>
}
